import { createContext, useContext, useEffect } from "react";

/**
 * Registry of the scroll-reset boundaries that are currently mounted, provided
 * by the `ScrollReset` component as a stable `Set` of pathname prefixes.
 *
 * @type {React.Context<Set<string> | null>}
 */
export const ScrollResetBoundaryContext = createContext(null);

/**
 * Marks `pathnamePrefix` as a tab shell for as long as the calling component
 * stays mounted. Navigations that stay inside the prefix — switching tabs —
 * keep the current scroll position; leaving it scrolls back to the top.
 *
 * Only register a prefix from the shell that owns the tab strip, so the
 * boundary disappears together with it.
 *
 * @param {string | null} pathnamePrefix e.g. `/animal/${animalId}`
 */
export const useScrollResetBoundary = (pathnamePrefix) => {
  const boundaries = useContext(ScrollResetBoundaryContext);

  useEffect(() => {
    if (boundaries == null || !pathnamePrefix) {
      return undefined;
    }

    boundaries.add(pathnamePrefix);
    return () => {
      boundaries.delete(pathnamePrefix);
    };
  }, [boundaries, pathnamePrefix]);
};
